import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Zap, Package, Truck, Warehouse, TrendingUp } from 'lucide-react';
import { toast } from 'react-toastify';
import { CardStats, ConfirmModal } from '@components/ui';
import MaterialPageLayout from '../../components/MaterialPageLayout';
import MaterialDetailModal from '../../components/MaterialDetailModal';
import StokSekme from '../../components/StokSekme';
import StokTablosu from '../../components/StokTablosu';
import StokAktarModal from '../../components/StokAktarModal';
import { formatCurrency } from '../../components/StockIndicator';
import ElektrikTable from './ElektrikTable';
import ElektrikModal from './ElektrikModal';
import GenelStokModal from '../components/GenelStokModal';
import { elektrikKategorileri, getElektrikStats, getElektrikByKategori } from '@mocks/materials/elektrik';
import { getStokHavuzu } from '@mocks/materials/stokHavuzu';
import { getHareketler, hareketTipleri } from '@mocks/materials/stokHareketleri';
import OdemePlanMiniModal from '@components/shared/OdemePlanMiniModal';
import { addMockCariHareket } from '@mocks/cariHesaplar';

export default function Elektrik() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);

    const [activeTab, setActiveTab] = useState('kullanim');
    const [filterDurum, setFilterDurum] = useState('');
    const [filterKategori, setFilterKategori] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [isStokOpen, setIsStokOpen] = useState(false);
    const [isAktarOpen, setIsAktarOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isOdemePlanOpen, setIsOdemePlanOpen] = useState(false);
    const [odemePlanData, setOdemePlanData] = useState(null);

    const insaatId = showAllConstructions ? null : selectedConstruction?.id;
    let data = getElektrikByKategori(filterKategori, insaatId);
    if (filterDurum) data = data.filter(i => i.durum === filterDurum);
    const stats = getElektrikStats(insaatId);
    const stokHavuzuData = getStokHavuzu('elektrik');
    const hareketlerData = getHareketler('elektrik');

    const handleAdd = () => { setSelectedItem(null); setIsModalOpen(true); };
    const handleView = (item) => { setSelectedItem(item); setIsDetailOpen(true); };
    const handleEdit = (item) => { setSelectedItem(item); setIsModalOpen(true); };
    const handleDelete = (item) => { setSelectedItem(item); setIsDeleteOpen(true); };
    const handleUpdateStock = (item) => { setSelectedItem(item); setIsStokOpen(true); };

    const handleSubmit = async (fd, isEdit) => {
        setIsLoading(true);
        await new Promise(r => setTimeout(r, 800));
        if (!isEdit && fd.supplierId && fd.kdvDahilToplam > 0) {
            addMockCariHareket(fd.supplierId, insaatId || 'c1', { type: 'purchase', subtype: 'fatura', amount: fd.kdvDahilToplam, date: fd.siparisTarihi || new Date().toISOString().split('T')[0], description: fd.autoDescription || `Elektrik — ${fd.miktar} ${fd.birim}` });
        }
        toast.success(isEdit ? 'Elektrik malzemesi güncellendi' : 'Yeni elektrik malzemesi eklendi');
        setIsLoading(false); setIsModalOpen(false);
        if (!isEdit && fd.supplierId && fd.kdvDahilToplam > 0) { setOdemePlanData({ amount: fd.kdvDahilToplam, companyName: fd.tedarikci }); setIsOdemePlanOpen(true); }
    };

    const handleConfirmDelete = async () => {
        setIsLoading(true);
        await new Promise(r => setTimeout(r, 500));
        toast.success('Elektrik malzemesi silindi');
        setIsLoading(false); setIsDeleteOpen(false);
    };

    const handleStokUpdate = async (stokData) => {
        setIsLoading(true);
        await new Promise(r => setTimeout(r, 500));
        toast.success(`Stok güncellendi: ${stokData.stokMiktar}`);
        setIsLoading(false); setIsStokOpen(false);
    };
    const handleAktarim = async (d) => { setIsLoading(true); await new Promise(r => setTimeout(r, 500)); toast.success(`${d.miktar} adet stok aktarıldı`); setIsLoading(false); setIsAktarOpen(false); };
    const handleOdemePlanConfirm = ({ nextDueDate }) => { toast.success(`Ödeme planı oluşturuldu — Vade: ${nextDueDate}`); setIsOdemePlanOpen(false); setOdemePlanData(null); };

    const statsCards = [
        { title: 'Toplam Kalem', value: `${stats.toplamKalem}`, icon: Package, color: 'primary' },
        { title: 'Teslim Edilen', value: `${stats.teslimEdilen}`, icon: Truck, color: 'success' },
        { title: 'Bekleyen', value: `${stats.bekleyen}`, icon: Warehouse, color: 'warning' },
        { title: 'Toplam Maliyet', value: formatCurrency(stats.toplamMaliyet), icon: TrendingUp, color: 'info' },
    ];

    const detailFields = [
        { key: 'siparisTarihi', label: 'Sipariş Tarihi', type: 'date' },
        { key: 'kategori', label: 'Kategori' },
        { key: 'urunAdi', label: 'Ürün Adı' },
        { key: 'aciklama', label: 'Açıklama', fullWidth: true },
        { key: 'miktar', label: 'Miktar', type: 'number' },
        { key: 'stokMiktar', label: 'Stok', type: 'number' },
        { key: 'toplamFiyat', label: 'Tutar', type: 'currency' },
        { key: 'durum', label: 'Durum', type: 'status' },
    ];

    const FiltersComponent = (
        <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[150px]">
                <label className="block text-xs text-text-muted mb-1">Durum</label>
                <select value={filterDurum} onChange={(e) => setFilterDurum(e.target.value)} className="w-full px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary">
                    <option value="">Tümü</option>
                    <option value="teslim">Teslim Edildi</option>
                    <option value="bekliyor">Bekliyor</option>
                </select>
            </div>
            <div className="flex-1 min-w-[150px]">
                <label className="block text-xs text-text-muted mb-1">Kategori</label>
                <select value={filterKategori} onChange={(e) => setFilterKategori(e.target.value)} className="w-full px-3 py-2 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary">
                    <option value="">Tümü</option>
                    {elektrikKategorileri.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                </select>
            </div>
        </div>
    );

    const headerFilter = <StokSekme activeTab={activeTab} onTabChange={setActiveTab} />;

    return (
        <>
            <MaterialPageLayout title="Elektrik" subtitle="Tesisat & Montaj" icon={Zap} iconBgColor="bg-error" backLink="/insaat/tesisat" stats={statsCards} onAddClick={activeTab === 'kullanim' ? handleAdd : undefined} filters={activeTab === 'kullanim' ? FiltersComponent : undefined} headerFilter={headerFilter}>
                {activeTab === 'kullanim' ? (
                    <ElektrikTable data={data} onView={handleView} onEdit={handleEdit} onDelete={handleDelete} onUpdateStock={handleUpdateStock} />
                ) : (
                    <StokTablosu stokData={stokHavuzuData} hareketData={hareketlerData} hareketTipleri={hareketTipleri} birim="adet" onAktarim={() => setIsAktarOpen(true)} />
                )}
            </MaterialPageLayout>

            <ElektrikModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={handleSubmit} initialData={selectedItem} isLoading={isLoading} />
            <MaterialDetailModal isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} data={selectedItem} fields={detailFields} title="Elektrik Malzemesi Detayı" />
            <GenelStokModal isOpen={isStokOpen} onClose={() => setIsStokOpen(false)} onSubmit={handleStokUpdate} data={selectedItem} isLoading={isLoading} />
            <StokAktarModal isOpen={isAktarOpen} onClose={() => setIsAktarOpen(false)} onSubmit={handleAktarim} malzemeTipi="elektrik" stokData={stokHavuzuData} birim="adet" isLoading={isLoading} />
            <ConfirmModal isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} onConfirm={handleConfirmDelete} title="Malzemeyi Sil" message={`"${selectedItem?.urunAdi}" malzemesini silmek istediğinize emin misiniz?`} isLoading={isLoading} />
            <OdemePlanMiniModal isOpen={isOdemePlanOpen} onClose={() => { setIsOdemePlanOpen(false); setOdemePlanData(null); }} onConfirm={handleOdemePlanConfirm} amount={odemePlanData?.amount} companyName={odemePlanData?.companyName} />
        </>
    );
}
